import React from "react";
import { Link } from "react-router-dom";
import "./index.css";
import retailPhoto from "./assets/nori-retail.jpg";
import retailJourneyPhoto from "./assets/nori-retail-journey.jpg";
import { posts } from "./caseStudyData";

export default function HomePage() {
  const workflow = [
    {
      number: "01",
      title: "Give customers a place to share",
      body: "Bring customer-created reels and product stories into a branded destination, or into the app your customers already use.",
    },
    {
      number: "02",
      title: "Make participation manageable",
      body: "Review submissions before they go live, set reward rules, and give your team a practical way to run the community.",
    },
    {
      number: "03",
      title: "Connect discovery to the next visit",
      body: "Link content to products, menu items or stays, using the ordering and booking flows the business already owns. Connections are configured per partner.",
    },
  ];

  // Break-even = monthly price / about $25 per paid order, rounded to a whole order.
  const plans = [
    { name: "Starter", price: "$99", uploads: "5 uploads a day", viewers: "100 viewers a day", extra: "Basic usage stats", payback: 4 },
    { name: "Growth", price: "$349", uploads: "20 uploads a day", viewers: "1,000 viewers a day", extra: "Repeat-order reporting", payback: 14, featured: true },
    { name: "Scale", price: "$999", uploads: "50 uploads a day", viewers: "Unlimited viewers", extra: "Lift measured against a holdout group", payback: 40 },
  ];

  return (
    <main>
      <section className="hero" id="home">
        <div className="hero-inner page-shell">
          <div className="hero-copy">
            <p className="eyebrow"><span className="eyebrow-dot" /> FOR RETAIL, HOSPITALITY AND RESTAURANTS</p>
            <h1>Give your customers a place to <em>come back to.</em></h1>
            <p className="hero-description">
              Nori helps businesses build and operate a community of their own,
              where customers share what they love and participation becomes
              a reason to return.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href="#pricing">See pricing</a>
              <a className="text-link" href="#workflow">How it works</a>
            </div>
            <p className="hero-footnote">Built for the customer app you already have.</p>
          </div>

          <div className="concept-wrap" aria-label="Illustrative Nori community product concept">
            <div className="concept-label">ILLUSTRATIVE PRODUCT CONCEPT</div>
            <div className="community-window">
              <div className="community-topbar">
                <span className="mini-brand"><span className="mini-mark">n</span> nori</span>
                <span className="store-label">FIELDNOTES / COMMUNITY</span>
                <span className="member-avatar">JL</span>
              </div>
              <div className="community-title-row">
                <div>
                  <p className="feed-kicker">THE FIELDNOTES COMMUNITY</p>
                  <h2>Worn your way.</h2>
                </div>
                <span className="feed-filter">For you</span>
              </div>
              <div className="feed-post">
                <div className="post-meta"><span className="post-avatar avatar-coral">M</span><span><strong>Maya L.</strong><small>Portland, OR</small></span><span className="post-time">2h</span></div>
                <div className="post-art post-art-one"><img src={retailPhoto} alt="Clothing displayed in an independent retailer" /><span>FIELD<br />TESTED</span><i>01</i></div>
                <div className="post-caption"><strong>Layers for the long way home.</strong><span>Liked by 28 neighbors</span></div>
                <div className="product-tag"><span className="product-swatch" /><span>Trail overshirt</span><strong>View item</strong></div>
              </div>
              <div className="feed-post feed-post-secondary">
                <div className="post-meta"><span className="post-avatar avatar-green">A</span><span><strong>Amir R.</strong><small>Seattle, WA</small></span><span className="post-time">5h</span></div>
                <div className="post-art post-art-two"><img src={retailJourneyPhoto} alt="Unbranded apparel arranged in a clothing shop" /><span>MADE<br />TO MOVE</span></div>
                <div className="post-caption"><strong>One jacket, three neighborhoods.</strong><span>Reward eligible</span></div>
              </div>
              <div className="community-footer"><span>Home</span><span>Discover</span><span className="footer-active">Community</span><span>Rewards</span></div>
            </div>
            <div className="review-note"><span className="review-check">✓</span><span><strong>Business reviewed</strong><small>Before publishing</small></span></div>
          </div>
        </div>
        <div className="hero-bottom page-shell">
          <span>ONE BUSINESS-OWNED DESTINATION</span><span className="hero-bottom-line" /><span>YOUR BRAND. YOUR COMMUNITY. YOUR CUSTOMER RELATIONSHIP.</span>
        </div>
      </section>

      <section className="intro-band">
        <div className="page-shell intro-grid">
          <p className="eyebrow">AFTER THE FIRST PURCHASE</p>
          <p className="intro-statement">Acquisition gets someone through the door. <strong>Nori gives the relationship somewhere to grow.</strong></p>
          <p className="intro-aside">Social can help people find you. Your community can give them a reason to return.</p>
        </div>
      </section>

      <section className="section-block workflow-section" id="workflow">
        <div className="page-shell">
          <div className="section-heading">
            <p className="eyebrow">THE OPERATING WORKFLOW</p>
            <h2>More than a feed.<br /><em>A way to run the whole loop.</em></h2>
            <p>Customer experience on one side. The tools your team needs on the other.</p>
          </div>
          <div className="workflow-grid">
            {workflow.map((step) => (
              <article className="workflow-item" key={step.number}>
                <span className="workflow-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
          <div className="workflow-tools">
            <span>CONTENT FEEDS</span><span>UPLOADS + MODERATION</span><span>REWARDS + EVENTS</span><span>DISCOVERY LINKS</span><span>BUSINESS INTEGRATIONS</span>
          </div>
        </div>
      </section>

      <section className="section-block segments-section" id="segments">
        <div className="page-shell">
          <div className="segments-head">
            <div>
              <p className="eyebrow">WHO IT IS FOR</p>
              <h2>One platform.<br /><em>Three kinds of business.</em></h2>
            </div>
            <p>The same product and the same three plans for every segment. Each case study walks through one.</p>
          </div>
          <div className="segments-grid">
            {posts.map((post) => (
              <article className="segment-card" key={post.slug}>
                <img src={post.image} alt={post.imageAlt} loading="lazy" />
                <h3>{post.segment}</h3>
                <p>{post.dek}</p>
                <Link className="text-link" to={`/case-studies/${post.slug}`}>Read the case study</Link>
              </article>
            ))}
          </div>
          <p className="caveat segments-note">Illustrations and concept workflows. No customer deployments are shown or implied.</p>
        </div>
      </section>

      <section className="section-block pricing-section" id="pricing">
        <div className="page-shell">
          <div className="pricing-head">
            <div>
              <p className="eyebrow">PRICING</p>
              <h2>Start small.<br /><em>Grow into what pays back.</em></h2>
            </div>
            <p>The same three plans for retail, hospitality and restaurants. Businesses already pay to find customers; Nori is priced against the cost of bringing them back. As an example, $8,000 a month in ads wins about 320 new customers at roughly $25 each.</p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan) => (
              <article className={plan.featured ? "plan is-featured" : "plan"} key={plan.name}>
                <h3>{plan.name}</h3>
                <p className="plan-price"><strong>{plan.price}</strong><span> / month</span></p>
                <ul>
                  <li>{plan.uploads}</li>
                  <li>{plan.viewers}</li>
                  <li>{plan.extra}</li>
                </ul>
                <p className="plan-payback">Pays back at about <strong>{plan.payback}</strong> extra repeat orders{"\u00a0"}a{"\u00a0"}month</p>
              </article>
            ))}
          </div>
          <div className="pricing-foot">
            <p>Extra uploads $0.30 each. Annual prepay saves 20%. Every plan starts with a paid 30-day pilot.</p>
            <Link className="button button-primary" to="/contact">Start a pilot <span aria-hidden="true">↗</span></Link>
          </div>
          <p className="caveat pricing-caveat">Launch pricing for design partners, subject to change. Payback assumes about $25 per paid order, a restaurant benchmark from published Meta and Google ad data (WordStream 2025). Retail typically pays more per order, about $38 on Meta (Triple Whale 2025), so it pays back sooner. Illustrative, not a Nori result. Reporting features are in development.</p>
        </div>
      </section>

      <section className="section-block market-section" id="market">
        <div className="page-shell">
          <div className="market-head">
            <div>
              <p className="eyebrow">MARKET</p>
              <h2>Sized from the price list.</h2>
            </div>
            <p>Retail, hospitality and restaurant businesses, at a blended price of about $3,200 a year each.</p>
          </div>
          <div className="market-row">
            <div className="market-cell"><span>SERVICEABLE MARKET</span><strong>$1.4B</strong><p>About 455,000 US businesses assumed ready for a customer app or program.</p></div>
          </div>
          <p className="caveat market-note">Planning estimate. Blended price assumes 60% Starter, 30% Growth and 10% Scale ($264 a month). The ready share is a 25% assumption applied to about 1.8 million US businesses (Census County Business Patterns, BLS, National Restaurant Association, AHLA), anchored on 20 to 44% of restaurants running loyalty programs (PYMNTS, 2022). Neither the plan mix nor willingness to pay has been validated.</p>
        </div>
      </section>

      <section className="section-block stage-section" id="stage">
        <div className="page-shell stage-grid">
          <div>
            <p className="eyebrow">WHERE THE PRODUCT STANDS</p>
            <h2>Useful workflows.<br /><em>Important proof still ahead.</em></h2>
            <p className="stage-copy">Current product materials describe an Android beta, a feed workflow, a rewards engine, and business moderation tools. Integrations still need partner configuration and implementation.</p>
            <p className="caveat">Capabilities described in product materials are not customer outcomes or proof of product-market fit.</p>
          </div>
          <div className="milestone-panel">
            <div className="panel-heading"><span>NEXT PROOF POINTS</span><span>TARGETS</span></div>
            <div className="milestone"><span className="milestone-mark">01</span><strong>Package SDK v1</strong><span>Product</span></div>
            <div className="milestone"><span className="milestone-mark">02</span><strong>Recruit two design partners</strong><span>Partners</span></div>
            <div className="milestone"><span className="milestone-mark">03</span><strong>Complete one measured pilot</strong><span>Evidence</span></div>
            <div className="milestone-foot">Measure repeat engagement against an appropriate comparison, including reward and moderation costs.</div>
          </div>
        </div>
      </section>

    </main>
  );
}
